/**
 * <toast-message> - Affichage de notifications temporaires
 */

import { announceToScreenReader } from '@/lib/accessibility.js';

function shouldCaptureConsoleLogs() {
  try {
    const isDev = typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.DEV;
    if (!isDev) return false;
    return window?.localStorage?.getItem('debug:console-capture') === '1';
  } catch {
    return false;
  }
}

class ToastMessage extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.queue = [];
    this.isShowing = false;
  }

  connectedCallback() {
    this.render();
    if (shouldCaptureConsoleLogs()) {
      this.setupConsoleCapture();
    }
  }

  /**
   * Met en place la capture des logs de console pour le débogage
   */
  setupConsoleCapture() {
    // Créer un tableau global pour stocker les logs récents
    if (!window.capturedLogs) {
      window.capturedLogs = [];
      const maxLogs = 50; // Garder seulement les 50 derniers logs
      
      // Sauvegarder les méthodes originales
      // eslint-disable-next-line no-console
      const originalLog = console.log;
      const originalError = console.error;
      const originalWarn = console.warn;
      
      // Surcharger console.log
      // eslint-disable-next-line no-console
      console.log = function(...args) {
        originalLog.apply(console, args);
        const message = args.map(arg => 
          typeof arg === 'object' ? JSON.stringify(arg, null, 2) : String(arg)
        ).join(' ');
        window.capturedLogs.push({
          type: 'log',
          message: message,
          timestamp: new Date().toISOString()
        });
        if (window.capturedLogs.length > maxLogs) {
          window.capturedLogs.shift();
        }
      };
      
      // Surcharger console.error
      console.error = function(...args) {
        originalError.apply(console, args);
        const message = args.map(arg => 
          typeof arg === 'object' ? JSON.stringify(arg, null, 2) : String(arg)
        ).join(' ');
        window.capturedLogs.push({
          type: 'error',
          message: message,
          timestamp: new Date().toISOString()
        });
        if (window.capturedLogs.length > maxLogs) {
          window.capturedLogs.shift();
        }
      };
      
      // Surcharger console.warn
      console.warn = function(...args) {
        originalWarn.apply(console, args);
        const message = args.map(arg => 
          typeof arg === 'object' ? JSON.stringify(arg, null, 2) : String(arg)
        ).join(' ');
        window.capturedLogs.push({
          type: 'warn',
          message: message,
          timestamp: new Date().toISOString()
        });
        if (window.capturedLogs.length > maxLogs) {
          window.capturedLogs.shift();
        }
      };
    }
  }

  /**
   * Affiche un toast
   * @param {string} message
   * @param {string} [type='info'] - 'success', 'error', 'warning', 'info'
   * @param {number} [duration=4000] - Durée en ms (ignorée pour les erreurs)
   */
  show(message, type = 'info', duration = 4000) {
    this.queue.push({ message, type, duration });
    if (!this.isShowing) {
      this.showNext();
    }
  }

  async showNext() {
    if (this.queue.length === 0) {
      this.isShowing = false;
      return;
    }

    this.isShowing = true;
    const { message, type, duration } = this.queue.shift();

    const toast = this.shadowRoot.querySelector('.toast');
    const messageEl = this.shadowRoot.querySelector('.toast-message');
    const iconEl = this.shadowRoot.querySelector('.toast-icon');
    const actionsEl = this.shadowRoot.querySelector('.toast-actions');

    // Définir le type et l'icône
    toast.className = `toast toast-${type}`;
    iconEl.textContent = this.getIcon(type);
    messageEl.textContent = message;

    // Afficher les actions uniquement pour les erreurs
    if (type === 'error') {
      actionsEl.style.display = 'flex';
      actionsEl.innerHTML = `
        <button class="toast-btn toast-copy" title="Copier l'erreur + les infos de debug">📋 Copier tout</button>
        <button class="toast-btn toast-close" title="Fermer">✕</button>
      `;
      
      // Ajouter les événements
      actionsEl.querySelector('.toast-copy').addEventListener('click', () => {
        // Récupérer tous les logs de la console
        
        // Essayer de récupérer les logs récents de la console
        try {
          // Chercher les logs dans la console actuelle
          if (typeof console !== 'undefined' && console.warn) {
            // Créer un message complet avec les logs de debug disponibles
            const debugInfo = [];
            
            // Chercher les éléments de debug génériques dans la page
            const debugElements = document.querySelectorAll('[data-debug], [data-error], .debug-info');
            if (debugElements.length > 0) {
              debugInfo.push('=== AUTRES DEBUG ELEMENTS ===');
              debugElements.forEach((el, index) => {
                debugInfo.push(`Element ${index + 1}: ${el.textContent || el.innerText}`);
              });
            }
            
            // Essayer de capturer les logs récents de la console
            try {
              if (window.capturedLogs && window.capturedLogs.length > 0) {
                debugInfo.push('=== CONSOLE LOGS RECENTS ===');
                // Prendre les 15 derniers logs avec les types
                const recentLogs = window.capturedLogs.slice(-15);
                recentLogs.forEach(log => {
                  const prefix = log.type === 'error' ? '❌' : log.type === 'warn' ? '⚠️' : 'ℹ️';
                  debugInfo.push(`${prefix} [${log.timestamp}] ${log.message}`);
                });
              }
            } catch (e) {
              debugInfo.push('Erreur capture logs console:', e.message);
            }
            
            // Construire le message complet
            const fullMessage = `
=== ERREUR APPLICATION ===
${message}

=== DEBUG CONSOLE ===
${debugInfo.length > 0 ? debugInfo.join('\n') : 'Aucun debug disponible dans le DOM'}

=== INFORMATIONS NAVIGATEUR ===
URL: ${window.location.href}
User Agent: ${navigator.userAgent}
Timestamp: ${new Date().toISOString()}
=== FIN ===
`.trim();
            
            navigator.clipboard.writeText(fullMessage).then(() => {
              // Indiquer visuellement que la copie a réussi
              const copyBtn = actionsEl.querySelector('.toast-copy');
              const originalText = copyBtn.textContent;
              copyBtn.textContent = '✓ Copié complet';
              setTimeout(() => {
                copyBtn.textContent = originalText;
              }, 2000);
            }).catch(() => {
              // Fallback si clipboard API n'est pas disponible
              const textArea = document.createElement('textarea');
              textArea.value = fullMessage;
              document.body.appendChild(textArea);
              textArea.select();
              document.execCommand('copy');
              document.body.removeChild(textArea);
              
              const copyBtn = actionsEl.querySelector('.toast-copy');
              const originalText = copyBtn.textContent;
              copyBtn.textContent = '✓ Copié complet';
              setTimeout(() => {
                copyBtn.textContent = originalText;
              }, 2000);
            });
          }
        } catch {
          // En cas d'erreur, copier uniquement le message d'erreur
          navigator.clipboard.writeText(message).then(() => {
            const copyBtn = actionsEl.querySelector('.toast-copy');
            const originalText = copyBtn.textContent;
            copyBtn.textContent = '✓ Copié (erreur seulement)';
            setTimeout(() => {
              copyBtn.textContent = originalText;
            }, 1500);
          });
        }
      });
      
      actionsEl.querySelector('.toast-close').addEventListener('click', () => {
        toast.classList.remove('show');
        setTimeout(() => this.showNext(), 300);
      });
    } else {
      actionsEl.style.display = 'none';
    }

    // Annoncer aux lecteurs d'écran
    announceToScreenReader(message, type === 'error' ? 'assertive' : 'polite');

    // Afficher
    toast.classList.add('show');

    // Masquer après le délai (uniquement pour les non-erreurs)
    if (type !== 'error') {
      setTimeout(() => {
        toast.classList.remove('show');
        setTimeout(() => this.showNext(), 300);
      }, duration);
    }
  }

  getIcon(type) {
    const icons = {
      success: '✓',
      error: '✕',
      warning: '⚠',
      info: 'ℹ',
    };
    return icons[type] || icons.info;
  }

  render() {
    this.shadowRoot.innerHTML = `
      <style>
        :host {
          position: fixed;
          top: var(--space-lg, 1.5rem);
          right: var(--space-lg, 1.5rem);
          z-index: var(--z-toast, 600);
          pointer-events: none;
        }

        .toast {
          display: flex;
          align-items: center;
          gap: var(--space-md, 1rem);
          min-width: 300px;
          max-width: 520px;
          padding: var(--space-md, 1rem) var(--space-xl, 2rem);
          background: color-mix(in srgb, var(--admin-bg, #f5efe6) 70%, #ffffff);
          color: var(--admin-text, #1f2933);
          border: 1px solid color-mix(in srgb, var(--admin-border, rgba(15,23,42,0.12)) 60%, transparent);
          border-radius: var(--radius-xl, 1rem);
          box-shadow: 0 15px 35px rgba(15, 23, 42, 0.15);
          transform: translateX(calc(100% + var(--space-lg, 1.5rem)));
          opacity: 0;
          visibility: hidden;
          transition: transform var(--transition-base, 250ms ease), opacity 200ms ease, visibility 0s 250ms;
          pointer-events: auto;
          font-family: var(--font-family, "Inter", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif);
        }

        .toast.show {
          transform: translateX(0);
          opacity: 1;
          visibility: visible;
          transition: transform var(--transition-base, 250ms ease), opacity 200ms ease, visibility 0s;
        }

        .toast-icon {
          flex-shrink: 0;
          width: 32px;
          height: 32px;
          border-radius: var(--radius-full, 9999px);
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: var(--font-weight-bold, 700);
          color: var(--color-text-inverse, #ffffff);
          box-shadow: inset 0 0 0 2px rgba(255,255,255,0.35);
        }

        .toast-success .toast-icon {
          background-color: var(--color-success, #4caf50);
        }

        .toast-error .toast-icon {
          background-color: var(--color-error, #f44336);
        }

        .toast-warning .toast-icon {
          background-color: var(--color-warning, #ff9800);
        }

        .toast-info .toast-icon {
          background-color: var(--color-info, #2196f3);
        }

        .toast-message {
          flex: 1;
          color: var(--admin-text, #1f2933);
          font-size: var(--font-size-md, 1rem);
          line-height: var(--line-height-normal, 1.5);
          font-weight: var(--font-weight-medium, 500);
        }

        .toast-actions {
          display: none;
          gap: var(--space-xs, 0.25rem);
          margin-left: var(--space-sm, 0.5rem);
        }

        .toast-btn {
          padding: var(--space-xs, 0.25rem) var(--space-sm, 0.5rem);
          border: none;
          border-radius: var(--radius-sm, 0.25rem);
          font-size: var(--font-size-xs, 0.75rem);
          cursor: pointer;
          transition: all var(--transition-base, 250ms ease);
          background: rgba(15, 23, 42, 0.08);
          color: var(--admin-text, #1f2933);
          opacity: 0.8;
        }

        .toast-btn:hover {
          opacity: 1;
          background: var(--color-surface-variant-hover, rgba(44, 44, 44, 0.15));
        }

        .toast-copy {
          background: linear-gradient(135deg, var(--admin-primary, #d4a574), color-mix(in srgb, var(--admin-primary, #d4a574) 80%, #b2753c));
          color: #fff;
        }

        .toast-copy:hover {
          filter: brightness(0.95);
        }

        .toast-close {
          background: rgba(15, 23, 42, 0.65);
          color: #fff;
        }

        .toast-close:hover {
          background: rgba(15, 23, 42, 0.85);
        }

        @media (max-width: 768px) {
          :host {
            top: var(--space-md, 1rem);
            right: var(--space-md, 1rem);
            left: var(--space-md, 1rem);
          }

          .toast {
            min-width: unset;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .toast {
            transition: none;
          }
        }
      </style>

      <div class="toast" role="status" aria-live="polite" aria-atomic="true">
        <span class="toast-icon"></span>
        <span class="toast-message"></span>
        <div class="toast-actions"></div>
      </div>
    `;
  }
}

customElements.define('toast-message', ToastMessage);
